/* eslint-disable jsx-a11y/anchor-is-valid */
import React, { useEffect, useMemo, useState } from 'react';
import { useDispatch } from 'react-redux';
import { useParams } from 'react-router-dom';
import { initialMeetingDashboardPage } from '../../../app/page/meeting-dashboard/meeting-dashboard.actions';
import { pageStateSelectors } from '../../../app/page/page-state/page-state.selectors';
import { useSelect, useSelectWithParams } from '../../../utils/selector.utils';
import { PageLayout } from '../common/PageLayout';
import { MeetingDashboardNavbar } from '../common/navigation-bars/MeetigDashboardNavbar';
import { MeetingManagement } from './MeetingManagement';
import { MeetingParticipants } from './MeetingParticipants';

import { agendaItemSelectors } from '../../../app/resource/agenda-item/agenda-item.selectors';
import { meetingSimpleSelectors } from '../../../app/resource/meeting-simple/meeting-simple.selectors';
import { IMeetingSimple } from '../../../app/resource/meeting-simple/meeting-simple.types';
import { agendaItemsPageSelectors } from '../../../app/page/page-storage/agenda-items/agenda-items.selectors';
import { meetingTasksPageSelectors } from '../../../app/page/page-storage/meeting-tasks/meeting-tasks.selectors';
import { ActionItemsBoard } from './ActionItemsBoard';
import { Kpi } from './Kpi';
import { GoalsBoard } from './GoalsBoard';
import { Boards } from './Boards';
import { WidgetFullCalendar } from '../common/widgets/WidgetFullCalendar';
import { TeamsFiles } from './MeetingsShortcuts';
import { userSelectors } from '../../../app/resource/user/user.selectors';
import { isMemberPartOfMeeting, isMemberActiveInWorkspace } from '../../../utils/user.utils';
import { IUser } from '../../../app/resource/user/user.types';
import { ISimpleTasks } from '../../../app/page/page-storage/tasks-simple/tasks-simple.types';
import { IMeetingItem } from '../../../app/page/page-storage/meeting-tasks/meeting-tasks.types';
import MeetingArchives from './MeetingArchives';
import { ROCK_REVIEW, KPI } from '../../../app/resource/agenda-item/agenda-item.types';

export const MeetingDashboardPage = () => {
  const dispatch = useDispatch();
  const { meetingId } = useParams<{ meetingId: IMeetingSimple['id'] }>();

  const showLoader = useSelectWithParams(pageStateSelectors.selectPageInProgress, 'meetingDashboard');

  useEffect(() => {
    dispatch(initialMeetingDashboardPage(meetingId));
    //dispatch(chooseClient(meetingId, true));
  }, [dispatch, meetingId]);

  const meeting = useSelectWithParams(meetingSimpleSelectors.selectResourceById, meetingId) as IMeetingSimple;
  const agendaItemIds = useSelect(agendaItemsPageSelectors.selectAgendaItems);
  const agendaItems = useSelectWithParams(
    agendaItemSelectors.selectResourcesById,
    agendaItemIds[meetingId] ? agendaItemIds[meetingId] : [],
  );

  const [qi, setQi] = useState(false);
  const [kpi, setKpi] = useState(false);

  useEffect(() => {
    if (agendaItems) {
      const kpiItem = agendaItems.filter((ai) => ai && ai.keyName === KPI);
      const qiItem = agendaItems.filter((ai) => ai && ai.keyName === ROCK_REVIEW);

      if (kpiItem[0]) {
        if (kpiItem[0].checked === '1') {
          setKpi(true);
        } else {
          setKpi(false);
        }
      }
      if (qiItem[0]) {
        if (qiItem[0].checked === '1') {
          setQi(true);
        } else {
          setQi(false);
        }
      }
    }
  }, [agendaItems]);

  const pathname = window.location.href;
  if (pathname.endsWith('#kpi')) {
    const kpiElement = document.getElementById('kpi');
    if (kpiElement) {
      kpiElement.scrollIntoView({ behavior: 'smooth' });
    }
  }

  const shareWithIds = meeting?.shareWithIds.filter((shareId) => !meeting?.administratorIds.includes(shareId));

  const meetingTasks = useSelect(meetingTasksPageSelectors.selectMeetingTasks)[meetingId];

  const filterUsersWithActiveElements = (meetingTaskElements: IMeetingItem[], userIds: string[]) => {
    const entries = (Object.entries(meetingTaskElements) as unknown) as [string, ISimpleTasks][];
    for (const entry of entries) {
      const userId = entry[0];
      const elements = entry[1];
      if (elements.active.length > 0) {
        userIds.push(userId);
      }
    }
    return userIds;
  };

  // This filters users that only have active goals,
  // allowing non-meeting users with active goals to be listed
  let userWithGoals: string[] = [];
  if (meetingTasks) {
    userWithGoals = filterUsersWithActiveElements(meetingTasks.goals, userWithGoals);
  }

  // This filters users that only have active action items,
  // allowing non-meeting users with active action items to be listed
  let userWithActionItems: string[] = [];
  if (meetingTasks) {
    userWithActionItems = filterUsersWithActiveElements(meetingTasks.actionItems, userWithActionItems);
  }

  const actionItemsUserIds = useMemo(
    () =>
      meeting
        ? meeting.type === 'meeting'
          ? Array.from(new Set([...userWithActionItems, ...meeting.administratorIds, ...shareWithIds]))
          : Array.from(new Set([...userWithActionItems, ...shareWithIds, ...meeting.administratorIds]))
        : [],
    [meeting, shareWithIds, userWithActionItems],
  );

  const order = (users: IUser[]) => {
    return users
      .sort((a: IUser, b: IUser) => {
        if (
          (!isMemberPartOfMeeting(a, meeting) || !isMemberActiveInWorkspace(a)) &&
          isMemberPartOfMeeting(b, meeting) &&
          isMemberActiveInWorkspace(b)
        ) {
          return 1;
        }
        return -1;
      })
      .map((user) => user.id);
  };

  const actionItemsUsers = useSelectWithParams(
    userSelectors.selectResourcesById,
    actionItemsUserIds,
  ) as (IUser | null)[];

  const existentUsers = actionItemsUsers.filter((user) => user) as IUser[];
  const existentActionItemUsersIds = order(existentUsers);

  const goalsUserIds = useMemo(
    () =>
      meeting
        ? meeting.type === 'meeting'
          ? Array.from(new Set([...userWithGoals, ...meeting.administratorIds, ...shareWithIds]))
          : Array.from(new Set([...userWithGoals, ...shareWithIds, ...meeting.administratorIds]))
        : [],
    [meeting, shareWithIds, userWithGoals],
  );

  const goalUsers = useSelectWithParams(userSelectors.selectResourcesById, goalsUserIds) as (IUser | null)[];

  const existentGoalUsers = goalUsers.filter((user) => user) as IUser[];
  const existentGoalUsersIds = order(existentGoalUsers);

  return (
    <PageLayout
      showLoader={showLoader}
      smallSidebar
      externalLogicNavbar={<MeetingDashboardNavbar showLoader={showLoader} meetingId={meetingId} />}
    >
      {!showLoader ? (
        <>
          <div className="row mb-25">
            <div className="col-6">
              <MeetingManagement meetingId={meetingId} />
            </div>
            <div className="col-6">
              <MeetingParticipants meetingId={meetingId} />
            </div>
          </div>
          {meetingTasks && (
            <ActionItemsBoard
              userIds={existentActionItemUsersIds}
              actionItems={meetingTasks.actionItems}
              adminIds={meeting.administratorIds}
              meetingId={meetingId}
            />
          )}
          <Boards meetingId={meetingId} />

          {kpi && <Kpi meetingId={meetingId} />}
          {qi && meetingTasks && (
            <GoalsBoard
              userIds={existentGoalUsersIds}
              goals={meetingTasks.goals}
              adminIds={meeting.administratorIds}
              meetingId={meetingId}
            />
          )}

          <WidgetFullCalendar meetingId={meetingId} />
          <div className="row mb-25 mt-3">
            <TeamsFiles />
            <MeetingArchives />
          </div>
        </>
      ) : (
        <></>
      )}
    </PageLayout>
  );
};
